import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRelay,
  deleteGoogleDriveSource,
  deleteMicrosoftTenantSource,
  listDriveSources,
  listGoogleDriveSources,
  listMicrosoftTenantSources,
  listRelays,
  revokeRelay,
  startGoogleDriveSourceConnect,
  startMicrosoftTenantConsent,
} from '@/api/drive-sources-api';
import { drivesKeys } from './useDrivesQueries';

export const driveSourcesKeys = {
  all: ['drive-sources'] as const,
  list: (memberId: number) => [...driveSourcesKeys.all, 'list', memberId] as const,
  microsoft: (memberId: number) => [...driveSourcesKeys.all, 'microsoft', memberId] as const,
  relays: (memberId: number) => [...driveSourcesKeys.all, 'relays', memberId] as const,
  google: (memberId: number) => [...driveSourcesKeys.all, 'google', memberId] as const,
};

export const useDriveSources = (memberId: number) => {
  return useQuery({
    queryKey: driveSourcesKeys.list(memberId),
    queryFn: listDriveSources,
    enabled: memberId > 0,
    refetchInterval: 30_000,
  });
};

export const useMicrosoftTenantSources = (memberId: number) => {
  return useQuery({
    queryKey: driveSourcesKeys.microsoft(memberId),
    queryFn: listMicrosoftTenantSources,
    enabled: memberId > 0,
  });
};

export const useStartMicrosoftTenantConsent = () => {
  return useMutation({
    mutationFn: startMicrosoftTenantConsent,
  });
};

export const useDeleteMicrosoftTenantSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMicrosoftTenantSource,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: driveSourcesKeys.all });
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useGoogleDriveSources = (memberId: number) => {
  return useQuery({
    queryKey: driveSourcesKeys.google(memberId),
    queryFn: listGoogleDriveSources,
    enabled: memberId > 0,
  });
};

export const useStartGoogleDriveSourceConnect = () => {
  return useMutation({
    mutationFn: startGoogleDriveSourceConnect,
  });
};

export const useDeleteGoogleDriveSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteGoogleDriveSource,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: driveSourcesKeys.all });
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useRelays = (memberId: number, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: driveSourcesKeys.relays(memberId),
    queryFn: listRelays,
    enabled: memberId > 0,
    refetchInterval: options?.refetchInterval ?? 30_000,
  });
};

export const useCreateRelay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelay,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: driveSourcesKeys.all });
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useRevokeRelay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeRelay,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: driveSourcesKeys.all });
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};
